import React, { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { Swiper } from 'swiper'
import { SwiperOptions } from 'swiper/types'
import { Pagination, Navigation, A11y, Keyboard } from 'swiper/modules'
import Typography from '@toasttab/shared/src/components/atoms/Typography/Typography'
import 'swiper/css'
import 'swiper/css/pagination'
import { Swiper as SwiperCore } from 'swiper/types'
import { CardColumnsProps } from './Cards.d'
import Icon from '../Atoms/Icon'
import SliderPagination from '@toasttab/shared/src/components/molecules/SliderPagination/SliderPagination'

const CardColumns = ({
  layout,
  mobileSlider = false,
  sliderPagination,
  iconStyle,
  classes,
  cardClasses,
  itemsHeadingCss,
  cards
}: CardColumnsProps) => {
  const [cardSwiper, setCardSwiper] = useState<SwiperCore>(null)
  const [slides, setSlides] = useState<any[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const ref = useRef<any | null>(null)
  const cellStyles = classNames({
    'flex flex-col basis basis-[150px] my-auto [@media(max-width:991px)]:items-center [@media(min-width:992px)]:p-[.9375rem] md:py-4':
      iconStyle === 'block',
    'flex flex-col [@media(max-width:991px)]:[&:not(:last-child)]:mb-5 [@media(max-width:991px)]:items-center [@media(min-width:992px)]:pr-[.9375rem] [@media(min-width:992px)]:!pl-[2.35rem] [@media(min-width:992px)]:indent-[-1.65rem]':
      iconStyle === 'inline'
  })

  const removeAttributes = (swiper: SwiperCore) => {
    if (!swiper) return
    const slides = swiper.slides
    slides.forEach((slide) => {
      slide.removeAttribute('tabindex')
      slide.removeAttribute('role')
      slide.removeAttribute('aria-label')
    })
  }

  function initSwiper() {
    if (!loading) {
      return
    }

    setLoading(true)
    const swiperObj: SwiperOptions = {
      init: false,
      modules: [Pagination, Navigation, A11y, Keyboard],
      speed: 300,
      threshold: 30,
      grabCursor: true,
      spaceBetween: 16,
      centeredSlides: true,
      a11y: {
        enabled: true,
        slideLabelMessage: `Slide {{index}} of {{slidesLength}}`,
        scrollOnFocus: true
      },
      keyboard: {
        enabled: true,
        onlyInViewport: true
      },
      on: {
        afterInit: function (this: SwiperCore) {
          removeAttributes(this)
        },
        init: function (this: SwiperCore) {
          removeAttributes(this)
        },
        update: function (this: SwiperCore) {
          removeAttributes(this)
        },
        resize: function (this: SwiperCore) {
          removeAttributes(this)
        }
      },
      loop: false,
      enabled: cards.length > 1 ? true : false,
      slidesPerView: 1.25,
      observeParents: true,
      observeSlideChildren: true,
      updateOnWindowResize: true,
      breakpoints: {
        992: {
          enabled: false,
          slidesPerView: cards.length,
          centeredSlides: false
        }
      }
    }

    if (sliderPagination) {
      Object.assign(swiperObj, {
        pagination: {
          el: ref.current.querySelector(`.swiper-pagination`),
          clickable: false
        }
      })
    }
    const swiper = new Swiper(ref.current, swiperObj)

    swiper.on('init', function () {
      swiper.update()
    })

    swiper.on('slideChange', function () {
      const slide = swiper.slides[swiper.activeIndex]
      const lottie = slide.querySelector('.lottie')
      if (lottie) {
        // @ts-ignore
        lottie.click()
      }

      const video = slide.querySelector('video')
      if (video) {
        video.click()
      }
    })

    setCardSwiper(swiper)

    setLoading(false)
  }

  useEffect(() => {
    setSlides(cards)
  }, [])

  useEffect(() => {
    if (slides.length > 0) {
      initSwiper()
    }
  }, [slides])

  useEffect(() => {
    if (cardSwiper) {
      cardSwiper.init()
    }
  }, [cardSwiper])

  const multiCardClass = classNames({
    'swiper-slide': mobileSlider,
    'col-span-4 md:col-span-3 lg:col-span-3 mds-cards-columns__4':
      layout === 'fourColumnCards',
    'col-span-4 md:col-span-2 lg:col-span-4': layout === 'threeColumnCards',
    'col-span-4 md:col-span-3 lg:col-span-4': layout === 'twoColumnCards',
    'col-span-4 md:col-span-12': layout === 'oneColumnCards'
  })

  const renderCards = () =>
    cards.map((card: any, i: number) => {
      // If cardClasses is a string, use the same classes for all cards
      // If it's an array, use classes by index
      const cardCss =
        typeof cardClasses === 'object' && cardClasses.length
          ? cardClasses[i]
          : cardClasses
      // This class is for 2 column layouts to center them
      const cardClass = classNames(
        multiCardClass,
        cardCss,
        '[&>div]:h-full !h-auto',
        {
          'lg:col-start-3': layout === 'twoColumnCards' && i % 2 === 0
        }
      )
      return (
        <div className={cardClass} key={i}>
          <div
            className='[@media(min-width:992px)]:flex [@media(min-width:992px)]:flex-col [@media(max-width:991px)]:text-center 
            [@media(max-width:991px)]:border [@media(max-width:991px)]:border-light  
            [@media(max-width:991px)]:p-[1.5625rem] [@media(max-width:991px)]:rounded-lg'
          >
            {card.columnHeader ? (
              <Typography
                allowHtml
                as='h3'
                variation='h3'
                classes='p-[.9375rem] [@media(max-width:991px)]:!text-[31px] [@media(max-width:991px)]:!font-medium 
                  [@media(min-width:992px)]:bg-gray-0 [@media(min-width:992px)]:rounded-lg xl:p-[1.875rem]'
              >
                {card.columnHeader}
              </Typography>
            ) : null}
            {card.columnDescription ? (
              <Typography allowHtml as='p' classes='text-gray-100 p-[.9375rem]'>
                {card.columnDescription}
              </Typography>
            ) : null}
            {card.children.map((child: any, index: number) => {
              const iconBefore =
                iconStyle === 'inline'
                  ? '[@media(min-width:992px)]:before:mr-[.6rem] [@media(min-width:992px)]:before:top-[.115rem] [@media(min-width:992px)]:before:relative [@media(max-width:991px)]:before:block before:' +
                    child.icon
                  : ''
              const headingStyles =
                iconStyle === 'inline'
                  ? '!font-semibold !leading-[1.29] !text-[1.0625rem] [@media(min-width:992px)]:pt-3'
                  : '!font-medium'
              return (
                <div
                  key={index}
                  className={`${cellStyles} [@media(min-width:1280px)]:!pl-[1.875rem]
                  [@media(min-width:1280px)]:!pr-[2.063rem]`}
                >
                  {iconStyle === 'block' && <Icon iconType={child.icon} />}
                  {child.heading ? (
                    <Typography
                      allowHtml
                      as='h4'
                      classes={`${iconBefore} ${headingStyles} ${itemsHeadingCss} !font-effra [@media(max-width:991px)]:text-base [@media(min-width:992px)]:!text-md`}
                      variation='h4'
                    >
                      {child.heading}
                    </Typography>
                  ) : null}
                  {child.description ? (
                    <Typography
                      allowHtml
                      as='p'
                      classes='text-gray-100 [@media(min-width:992px)]:pb-[.9375rem]'
                    >
                      {child.description}
                    </Typography>
                  ) : null}
                </div>
              )
            })}
          </div>
        </div>
      )
    })

  const renderSlider = () =>
    mobileSlider ? (
      <div className='swiper !overflow-visible' ref={ref}>
        <div className='swiper-wrapper'>{renderCards()}</div>
        {sliderPagination ? <SliderPagination /> : null}
      </div>
    ) : (
      <div className='ds-grid'>{renderCards()}</div>
    )

  const wrapperClasses = classNames(
    'col-span-4 md:col-span-6 lg:col-span-12 mds-cards-columns [@media(min-width:992px)_and_(max-width:1199px)]:max-w-[960px] [@media(min-width:1200px)_and_(max-width:1439px)]:max-w-[1140px] [@media(min-width:1440px)]:max-w-[1236px] [@media(max-width:991px)]:cursor-grab',
    classes
  )

  return <div className={wrapperClasses}>{renderSlider()}</div>
}

export default CardColumns
